// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { Config } from "config";
import { BuildState } from "networking/api";
import { BuildType } from "networking/BuildType";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { addPipelineBuild } from "store/organization/actions";
import { DocSetAutoAssignCancelledSchema, DocSetAutoAssignClickedSchema, DocSetAutoAssignConfirmedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useAppDispatch } from 'hooks/redux';
import { isBuildActive } from "store/state_helpers";

export function useAutoAssignCommand(selectedItems: string[], totalItems: number, onComplete: (error?: any) => void): IDocumentCommand {
    const [isActive, setIsActive] = useState(false);

    const builds = useSelector((state: AppState) => state.organizations.pipelineBuilds.activeBuilds);
    const buildsFetched = useSelector((state: AppState) => state.organizations.pipelineBuilds.fetchState.fetched);
    const disabled = useSelector((state: AppState) => selectIsLimitedOrganization(state));
    const dispatch = useAppDispatch();

    function onExecute() {
        Telemetry.trackEvent<DocSetAutoAssignClickedSchema>({
            unassignedCount: totalItems,
            eventName: TelemetryEvent.DocSetAutoAssignClicked
        });

        setIsActive(true);
    }

    function onDismiss() {
        Telemetry.trackEvent<DocSetAutoAssignCancelledSchema>({
            eventName: TelemetryEvent.DocSetAutoAssignCancelled
        });
        finish();
    }

    async function onConfirm() {
        Telemetry.trackEvent<DocSetAutoAssignConfirmedSchema>({
            unassignedCount: totalItems,
            eventName: TelemetryEvent.DocSetAutoAssignConfirmed
        });

        const url = `${Config.apiUrl}/builds/clustering`;
        try {
            const response = await axios.post(url);
            dispatch(addPipelineBuild({
                state: BuildState.NotStarted,
                buildRequest:
                {
                    id: response.data,
                    clusterId: "",
                    buildType: BuildType.Clustering
                }
            }));

            finish();
        }
        catch (error) {
            finish(error);
        }
    }

    function finish(error?: any) {
        setIsActive(false);
        onComplete(error);
    }

    function getVisual() {
        if (!isActive) {
            return null;
        }

        return <ConfirmationDialog
            hidden={!isActive}
            title={"Auto Assign Documents"}
            subText={"Docugami will process and assign all of your documents to doc sets automatically."}
            confirmButtonText={"OK"}
            dismissButtonText={"Cancel"}
            dismissedCallback={onDismiss}
            confirmedCallback={onConfirm} />
    }

    function isEnabled() {
        if (!!disabled) {
            return false;
        }

        // Only allow auto assign if there is not an active clustering build
        const isClusteringActive = isBuildActive(BuildType.Clustering, builds);
        return (selectedItems.length === 0 || selectedItems.length === totalItems) &&
            totalItems > 0 &&
            buildsFetched &&
            !isClusteringActive;
    }

    return {
        id: "AutoAssign",
        label: "Auto Assign All",
        isActive: isActive,
        isEnabled: isEnabled(),
        visual: getVisual(),
        execute: onExecute
    }
}
